import {
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  type FormProps,
  Result,
  Select,
  Spin,
  theme,
  Typography,
} from 'antd';
import { Download, ExternalLink, Inbox } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { catchError, EMPTY, from, tap } from 'rxjs';
import { SegmentedTabs } from '../../../components/segmented-tabs';
import SyncSuspense from '../../../components/sync-suspense';
import { type MealCategory, type Route } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApi from '../../../hooks/use-secured-api';
import { useQuery } from '../../../jsonapi-provider';
import { LocalDate } from '../../../utils/local-date';

interface FiltersType {
  routes?: Array<Route['id']>;
  orderDate?: LocalDate;
  mealCategory?: Array<MealCategory['id']>;
}

type KeyValue = 'delivery-summary' | 'delivery-receipt';

const tabList: Array<{ key: KeyValue; tab: string }> = [
  {
    key: 'delivery-summary',
    tab: 'Delivery Summaries',
  },
  {
    key: 'delivery-receipt',
    tab: 'Delivery Receipts',
  },
];

const DeliveryReceiptsPage = (): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [activeTabKey, setActiveTabKey] = useState<KeyValue>(tabList[0].key);
  const api = useSecuredApi();
  const [reportUrl, setReportUrl] = useState<Record<KeyValue, string | undefined>>({
    'delivery-summary': '',
    'delivery-receipt': '',
  });
  const [fileNames, setFileNames] = useState<{ [P in KeyValue]: string }>({
    'delivery-summary': '',
    'delivery-receipt': '',
  });

  const openInNewWindow = (): void => {
    window.open(`${reportUrl[activeTabKey]}#toolbar=0`);
  };

  const downloadReport = (): void => {
    const url = reportUrl[activeTabKey];
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = fileNames[activeTabKey] || `${activeTabKey}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      fields: {
        'meal-category': ['name'],
      },
      sort: 'sortOrder',
      page: {
        size: 10000,
      },
    },
  ]);

  const routes = Array(99)
    .fill(0)
    .map((_, i) => ({ id: i + 1 }));

  const fetchSheet: FormProps<FiltersType>['onFinish'] = (submitted): void => {
    // setting the reportUrl to undefined will trigger the SyncSuspense fallback while the new report is being generated
    setReportUrl({ 'delivery-summary': undefined, 'delivery-receipt': undefined });

    const orderDate = submitted.orderDate?.toISOString();
    from(
      api.post(
        process.env.REACT_APP_DELIVERY_SUMMARY_ENDPOINT!,
        {
          orderDate: submitted.orderDate,
          hhcRouteNumbers: submitted.routes,
          mealCategoryIds: submitted.mealCategory,
        },
        {},
        {},
        true
      )
    )
      .pipe(
        tap(({ blob }) => {
          const fileName = `Delivery Summary - ${orderDate}.pdf`;
          const fileURL = blob ? URL.createObjectURL(blob) : '';
          setReportUrl((prev) => ({ ...prev, 'delivery-summary': fileURL }));
          setFileNames((prev) => ({ ...prev, 'delivery-summary': fileName }));
        }),
        catchError((error: Error) => {
          setReportUrl((prev) => ({ ...prev, 'delivery-summary': '' }));
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No delivery summaries available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
    from(
      api.post(
        process.env.REACT_APP_DELIVERY_RECEIPT_ENDPOINT!,
        {
          orderDate: submitted.orderDate,
          hhcRouteNumbers: submitted.routes,
          mealCategoryIds: submitted.mealCategory,
        },
        {},
        {},
        true
      )
    )
      .pipe(
        tap(({ blob }) => {
          const fileName = `Delivery Receipt - ${orderDate}.pdf`;
          const fileURL = blob ? URL.createObjectURL(blob) : '';
          setReportUrl((prev) => ({ ...prev, 'delivery-receipt': fileURL }));
          setFileNames((prev) => ({ ...prev, 'delivery-receipt': fileName }));
        }),
        catchError((error: Error) => {
          setReportUrl((prev) => ({ ...prev, 'delivery-receipt': '' }));
          const [description, title] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No delivery receipts available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex gap="middle" vertical>
      <Flex gap="middle" style={{ width: '100%' }}>
        <Flex flex="1" vertical>
          <Card styles={{ body: { padding: `${token.paddingSM}px ${token.paddingXS}px` } }}>
            <SyncSuspense
              fallback={
                <Flex style={{ height: 300 }} justify="center" align="center">
                  <Spin />
                </Flex>
              }
              condition={!mealCategories.isLoading}
            >
              <Flex gap="small" vertical>
                <Flex
                  style={{ paddingInline: token.paddingMD, paddingBlockStart: token.paddingSM }}
                  vertical
                >
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    Delivery Receipts
                  </Typography.Title>
                  <Typography.Text
                    style={{ marginBlockStart: token.marginXS, cursor: 'initial' }}
                    disabled
                  >
                    Generate and export delivery paperwork based on filters
                  </Typography.Text>
                </Flex>
                <Form
                  layout="vertical"
                  onFinish={fetchSheet}
                  initialValues={{ orderDate: new LocalDate(), site: ['all'] }}
                  style={{ paddingInline: token.paddingMD, marginBlockStart: token.marginMD }}
                >
                  <Form.Item<FiltersType>
                    name="orderDate"
                    label="Order date"
                    getValueProps={(value) => ({
                      value: value ? value.toDayjs() : null,
                    })}
                    normalize={(value) => (value ? new LocalDate(value) : null)}
                    rules={[{ required: true }]}
                  >
                    <DatePicker />
                  </Form.Item>

                  <Form.Item<FiltersType> name="routes" label="HHC Route Number">
                    <Select
                      allowClear
                      showSearch
                      mode="multiple"
                      options={routes}
                      placeholder="All Routes Numbers"
                      fieldNames={{ label: 'name', value: 'id' }}
                    />
                  </Form.Item>

                  <Form.Item<FiltersType> name="mealCategory" label="Meal Category">
                    <Select
                      allowClear
                      showSearch
                      mode="multiple"
                      options={mealCategories.data ?? []}
                      placeholder="All Meal Categories"
                      fieldNames={{ label: 'name', value: 'id' }}
                    />
                  </Form.Item>

                  <Form.Item<FiltersType> style={{ marginBlockEnd: 0 }}>
                    <Flex
                      style={{
                        flexDirection: 'row-reverse',
                        marginBlockStart: token.marginMD,
                      }}
                    >
                      <Button
                        htmlType="submit"
                        type="primary"
                        style={{
                          marginInlineStart: 'auto',
                          paddingInline: token.paddingLG,
                        }}
                      >
                        Generate
                      </Button>
                    </Flex>
                  </Form.Item>
                </Form>
              </Flex>
            </SyncSuspense>
          </Card>
        </Flex>

        <Flex flex="1" style={{ maxWidth: 600 }} vertical>
          <Card styles={{ body: { padding: 0 } }}>
            <Flex
              gap="small"
              style={{
                flexDirection: 'row-reverse',
                padding: token.paddingSM,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <Button
                block
                icon={<Download />}
                onClick={downloadReport}
                disabled={!reportUrl[activeTabKey]?.length}
              >
                Download
              </Button>
              <Button
                block
                icon={<ExternalLink />}
                onClick={openInNewWindow}
                disabled={!reportUrl[activeTabKey]?.length}
              >
                Open in new window
              </Button>
            </Flex>
            <Flex gap="small" justify="center" flex="1 1 100%" vertical>
              <SegmentedTabs
                onChange={(key) => {
                  setActiveTabKey(key as KeyValue);
                }}
                initialActiveKey={activeTabKey}
              >
                <SegmentedTabs.TabPane label="Delivery Summary" value="delivery-summary">
                  <SyncSuspense
                    fallback={
                      <Flex style={{ height: 300 }} justify="center" align="center">
                        <Spin />
                      </Flex>
                    }
                    condition={reportUrl['delivery-summary'] !== undefined}
                  >
                    <SyncSuspense
                      fallback={
                        <Result
                          title={<Typography.Title level={4}>Nothing to preview</Typography.Title>}
                          icon={<Inbox />}
                          style={{ fontSize: '3em' }}
                        />
                      }
                      condition={!!reportUrl['delivery-summary']?.length}
                    >
                      <iframe
                        src={`${reportUrl['delivery-summary']}#toolbar=0`}
                        style={{ border: 0, height: 'auto', aspectRatio: '210 / 297' }}
                        width="100%"
                      />
                    </SyncSuspense>
                  </SyncSuspense>
                </SegmentedTabs.TabPane>
                <SegmentedTabs.TabPane label="Delivery Receipt" value="delivery-receipt">
                  <SyncSuspense
                    fallback={
                      <Flex style={{ height: 300 }} justify="center" align="center">
                        <Spin />
                      </Flex>
                    }
                    condition={reportUrl['delivery-receipt'] !== undefined}
                  >
                    <SyncSuspense
                      fallback={
                        <Result
                          title={<Typography.Title level={4}>Nothing to preview</Typography.Title>}
                          icon={<Inbox />}
                          style={{ fontSize: '3em' }}
                        />
                      }
                      condition={!!reportUrl['delivery-receipt']?.length}
                    >
                      <iframe
                        src={`${reportUrl['delivery-receipt']}#toolbar=0`}
                        style={{ border: 0, height: 'auto', aspectRatio: '210 / 297' }}
                        width="100%"
                      />
                    </SyncSuspense>
                  </SyncSuspense>
                </SegmentedTabs.TabPane>
              </SegmentedTabs>
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default DeliveryReceiptsPage;
